/**
 * UPT-2079 L4 (web §5.10, rest §4.5) — projection d'UNE entité du Module Spec en instantané d'aperçu : miroir TypeScript de
 * `PreviewSnapshotBuilder.java`, utilisé par le DÉPÔT DE DÉMONSTRATION seulement (le serveur projette l'entité quand `preview`
 * est servi ; le web ne reçoit que l'instantané).
 *
 * Liste blanche stricte, lecture défensive (valeur absente ou d'un autre type ignorée, jamais d'exception) :
 * - `module` : `{code, name, locales}` ; jamais `provenance` (courriel de l'auteur), `rights`, `permissions`, `testPlan`,
 *   `demoData`, `documentation` ;
 * - `entity` : `{code, label{singular, plural}, fields[], screens}` ; champs `{name, type, label?, help?, required?, default?,
 *   mode?, validators?, data?}` ; JAMAIS `doc`, `storage`, `unique`, `grid`, `protection`, ni `workflow`, `rules`, `parent`,
 *   `table`, `slugs`, `scope`, `businessKey`, `upsert`, `permissionEntity`, `customRenderers` ; le `message` d'un validateur
 *   est retiré ; `data.store` et `data.entityref` sont réduits à leur type (aucun nom d'une autre entité) ;
 * - toute chaîne est coupée à `PREVIEW_LIMITS.snapshotStringMaxChars` (500) ; ≤ 200 champs, ≤ 50 options, ≤ 15 colonnes.
 */
import {
  PREVIEW_LIMITS,
  type PreviewI18nText,
  type PreviewSnapshot,
  type PreviewSnapshotData,
  type PreviewSnapshotField,
  type PreviewSnapshotScreens,
  type PreviewSnapshotValidator,
} from '../data/contracts';

/** Langues retenues dans l'instantané. */
export const PREVIEW_SNAPSHOT_LOCALES = ['fr', 'en', 'de', 'it', 'ar'] as const;

/** Les 19 types de champ du schéma du Module Spec (`$defs.field.type`). */
export const PREVIEW_FIELD_TYPES: readonly string[] = [
  'text',
  'textarea',
  'number',
  'amount',
  'date',
  'datetime',
  'time',
  'checkbox',
  'select',
  'radio',
  'typeahead',
  'email',
  'color',
  'link',
  'display',
  'hidden',
  'attachments',
  'references',
  'entityref',
];

/** Les dix types de validateur du schéma. */
export const PREVIEW_VALIDATOR_TYPES: readonly string[] = [
  'required',
  'maxLength',
  'minLength',
  'isNumber',
  'minValue',
  'maxValue',
  'pattern',
  'notEqual',
  'email',
  'avsNumber',
];

const FIELD_NAME_PATTERN = /^[a-z][a-z0-9_]{0,62}$/;
const MODES: readonly string[] = ['EDIT', 'READONLY', 'HIDDEN'];
const MAX_FIELDS = 200;
const MAX_OPTIONS = 50;
const MAX_COLUMNS = 15;

type Json = Record<string, unknown>;

const asRecord = (value: unknown): Json | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Json) : null;

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** Coupe à `max` unités UTF-16 SANS casser une paire de substitution (comme le serveur : une moitié de paire ne s'écrit pas en JSON). */
export const cutUtf16 = (text: string, max: number): string => {
  if (text.length <= max) return text;
  const last = text.charCodeAt(max - 1);
  const end = last >= 0xd800 && last <= 0xdbff ? max - 1 : max;
  return text.slice(0, end);
};

const cut = (text: string): string => cutUtf16(text, PREVIEW_LIMITS.snapshotStringMaxChars);

const asString = (value: unknown): string | null => (typeof value === 'string' ? cut(value) : null);

/** Texte d'un libellé : chaîne, sinon `fr`, sinon la première chaîne d'un objet langue. */
const plainText = (value: unknown): string => {
  const direct = asString(value);
  if (direct !== null) return direct;
  const record = asRecord(value);
  if (!record) return '';
  const fr = asString(record.fr);
  if (fr !== null) return fr;
  const first = Object.values(record).find((entry) => typeof entry === 'string');
  return typeof first === 'string' ? cut(first) : '';
};

/** `i18nText` : une chaîne (clé de traduction) reste une chaîne ; un objet langue est restreint aux cinq langues retenues. */
const i18nText = (value: unknown): PreviewI18nText | undefined => {
  const direct = asString(value);
  if (direct !== null) return direct;
  const record = asRecord(value);
  if (!record) return undefined;
  const out: Record<string, string> = {};
  PREVIEW_SNAPSHOT_LOCALES.forEach((locale) => {
    const text = asString(record[locale]);
    if (text !== null) out[locale] = text;
  });
  return Object.keys(out).length > 0 ? out : undefined;
};

const scalar = (value: unknown): string | number | boolean | undefined => {
  if (typeof value === 'string') return cut(value);
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'boolean') return value;
  return undefined;
};

const readValidators = (value: unknown): PreviewSnapshotValidator[] | undefined => {
  const out: PreviewSnapshotValidator[] = [];
  asArray(value).forEach((entry) => {
    const record = asRecord(entry);
    if (!record || typeof record.type !== 'string' || !PREVIEW_VALIDATOR_TYPES.includes(record.type)) return;
    const validator: PreviewSnapshotValidator = { type: record.type };
    const scalarValue = scalar(record.value);
    if (scalarValue !== undefined) validator.value = scalarValue;
    out.push(validator);
  });
  return out.length > 0 ? out : undefined;
};

const readData = (value: unknown): PreviewSnapshotData | undefined => {
  const record = asRecord(value);
  if (!record) return undefined;
  if (record.type === 'store') return { type: 'store' };
  if (record.type === 'entityref') return { type: 'entityref' };
  if (record.type !== 'static') return undefined;
  const options: { value: string; label: PreviewI18nText }[] = [];
  asArray(record.options).forEach((entry) => {
    if (options.length >= MAX_OPTIONS) return;
    const option = asRecord(entry);
    if (!option) return;
    const optionValue =
      typeof option.value === 'string'
        ? cut(option.value)
        : typeof option.value === 'number' && Number.isFinite(option.value)
          ? String(option.value)
          : null;
    if (optionValue === null) return;
    options.push({ value: optionValue, label: i18nText(option.label) ?? optionValue });
  });
  return { type: 'static', options };
};

const readField = (value: unknown): PreviewSnapshotField | null => {
  const record = asRecord(value);
  if (!record) return null;
  const name = typeof record.name === 'string' ? record.name : '';
  const type = typeof record.type === 'string' ? record.type : '';
  if (!FIELD_NAME_PATTERN.test(name) || !PREVIEW_FIELD_TYPES.includes(type)) return null;
  const field: PreviewSnapshotField = { name, type };
  const label = i18nText(record.label);
  if (label !== undefined) field.label = label;
  const help = i18nText(record.help);
  if (help !== undefined) field.help = help;
  if (typeof record.required === 'boolean') field.required = record.required;
  const defaultValue = scalar(record.default);
  if (defaultValue !== undefined) field.default = defaultValue;
  if (typeof record.mode === 'string' && MODES.includes(record.mode)) field.mode = record.mode as PreviewSnapshotField['mode'];
  const validators = readValidators(record.validators);
  if (validators) field.validators = validators;
  const data = readData(record.data);
  if (data) field.data = data;
  return field;
};

/** Noms de champs recopiés tels que nommés, au motif d'un nom de champ, sans recoupement avec les champs retenus (le web filtre). */
const readNames = (value: unknown, max: number): string[] => {
  const out: string[] = [];
  asArray(value).forEach((entry) => {
    if (out.length < max && typeof entry === 'string' && FIELD_NAME_PATTERN.test(entry)) out.push(entry);
  });
  return out;
};

const readFieldName = (value: unknown): string | null => (typeof value === 'string' && FIELD_NAME_PATTERN.test(value) ? value : null);

const readScreens = (value: unknown): PreviewSnapshotScreens | undefined => {
  const screens = asRecord(value);
  if (!screens) return undefined;
  const out: PreviewSnapshotScreens = {};
  const list = asRecord(screens.list);
  if (list) {
    const target: NonNullable<PreviewSnapshotScreens['list']> = {};
    const columns = readNames(list.columns, MAX_COLUMNS);
    if (columns.length > 0) target.columns = columns;
    const sort = asRecord(list.defaultSort);
    const sortField = readFieldName(sort?.field);
    if (sort && sortField) {
      target.defaultSort = { field: sortField };
      if (sort.direction === 'asc' || sort.direction === 'desc') target.defaultSort.direction = sort.direction;
    }
    const card = asRecord(list.mobileCard);
    if (card) {
      const mobileCard: NonNullable<NonNullable<PreviewSnapshotScreens['list']>['mobileCard']> = {};
      (['title', 'subtitle', 'badge'] as const).forEach((slot) => {
        const name = readFieldName(card[slot]);
        if (name) mobileCard[slot] = name;
      });
      if (Object.keys(mobileCard).length > 0) target.mobileCard = mobileCard;
    }
    if (Object.keys(target).length > 0) out.list = target;
  }
  const form = asRecord(screens.form);
  if (form) {
    const tabs: { key: string; label: PreviewI18nText; fields: string[] }[] = [];
    asArray(form.tabs).forEach((entry) => {
      const tab = asRecord(entry);
      const key = asString(tab?.key);
      if (!tab || !key) return;
      tabs.push({ key, label: i18nText(tab.label) ?? key, fields: readNames(tab.fields, MAX_FIELDS) });
    });
    if (tabs.length > 0) out.form = { tabs };
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

/**
 * Instantané de l'entité `entityCode` du document ; `null` quand l'entité est absente ou n'a aucun champ exploitable (le
 * serveur rend alors 400 `preview.dataset.invalid`, `entityCode`). Ne lève jamais.
 */
export const buildPreviewSnapshot = (spec: unknown, entityCode: string): PreviewSnapshot | null => {
  const document = asRecord(spec);
  const entity = asRecord(asRecord(document?.entities)?.[entityCode]);
  if (!document || !entity) return null;
  const fields: PreviewSnapshotField[] = [];
  const seen = new Set<string>();
  for (const raw of asArray(entity.fields)) {
    if (fields.length >= MAX_FIELDS) break;
    const field = readField(raw);
    // Un nom en double fausserait les clés des lignes : seule la première occurrence est retenue.
    if (field && !seen.has(field.name)) {
      seen.add(field.name);
      fields.push(field);
    }
  }
  const usable = fields.length > 0;
  if (!usable) return null;
  const module = asRecord(document.module);
  const declared = asArray(module?.locales).filter(
    (entry): entry is string => typeof entry === 'string' && (PREVIEW_SNAPSHOT_LOCALES as readonly string[]).includes(entry)
  );
  const locales = declared.length > 0 ? Array.from(new Set(declared)) : [...PREVIEW_SNAPSHOT_LOCALES];
  const label = asRecord(entity.label);
  const moduleCode = plainText(module?.code);
  const singular = i18nText(label?.singular) ?? cut(entityCode);
  const snapshot: PreviewSnapshot = {
    format: 1,
    module: { code: moduleCode, name: plainText(module?.name) || moduleCode, locales },
    entity: {
      code: entityCode,
      label: { singular, plural: i18nText(label?.plural) ?? cut(entityCode) },
      fields,
    },
  };
  const screens = readScreens(entity.screens);
  if (screens) snapshot.entity.screens = screens;
  return snapshot;
};

/** Octets UTF-8 du JSON de l'instantané (413 `preview.dataset.too_large` / `snapshot` au-delà de `PREVIEW_LIMITS.snapshotMaxBytes`). */
export const previewSnapshotBytes = (snapshot: PreviewSnapshot): number => new TextEncoder().encode(JSON.stringify(snapshot)).length;

export const isPreviewSnapshotTooLarge = (snapshot: PreviewSnapshot): boolean =>
  previewSnapshotBytes(snapshot) > PREVIEW_LIMITS.snapshotMaxBytes;
